import React from 'react';
import { Box, Typography, Grid, Chip } from '@mui/material';
import {
  SportsSoccer as BallIcon,
  AccessTime as TimeIcon,
  Stadium as StadiumIcon,
} from '@mui/icons-material';
import { COLORS, SECTION_STYLES } from './MAMCStyles';
import { styled } from '@mui/material/styles';

// Styled Card component
const StyledCard = styled(Box)(({ theme }) => ({
  backgroundColor: COLORS.background.light,
  color: COLORS.text.primary,
  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  borderRadius: theme.spacing(2),
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  border: `1px solid ${COLORS.background.light}`,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 12px 48px rgba(218,165,32,0.4)`,
    border: `1px solid ${COLORS.secondary}`,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${COLORS.secondary}, ${COLORS.accent})`,
  },
}));

const OngoingMatches = ({ matches, teams, scores, scorers }) => {
  return (
    <Box
      sx={{
        ...SECTION_STYLES.ongoingMatches,
        mb: { xs: 2, sm: 3, md: 4 },
        p: { xs: 2, sm: 3 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 3,
          pb: 2,
          borderBottom: `2px solid ${COLORS.background.light}`,
        }}
      >
        <TimeIcon
          sx={{
            color: COLORS.secondary,
            fontSize: { xs: 24, sm: 28 },
            filter: 'drop-shadow(0 2px 4px rgba(218,165,32,0.3))',
          }}
        />
        <Typography
          variant="h6"
          sx={{
            color: COLORS.text.accent,
            fontSize: { xs: '1rem', sm: '1.25rem' },
            textTransform: 'uppercase',
            letterSpacing: '1px',
            fontWeight: 'bold',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
          }}
        >
          Ongoing Matches
        </Typography>
      </Box>

      <Grid container spacing={{ xs: 4, sm: 3, md: 4 }}>
        {matches
          .filter(match => match.ongoing)
          .map(match => {
            const homeTeam = teams.find(t => t.id === match.home_team_id);
            const awayTeam = teams.find(t => t.id === match.away_team_id);
            const score = scores.find(s => s.match_id === match.id);
            const matchScorers = scorers.filter(s => s.match_id === match.id);

            // Filter scorers by team using player IDs
            const homeTeamScorers = matchScorers
              .filter(scorer => {
                const player = homeTeam?.players?.find(p => p.id === scorer.player_id);
                return !!player;
              })
              .sort((a, b) => a.score_minute - b.score_minute);

            const awayTeamScorers = matchScorers
              .filter(scorer => {
                const player = awayTeam?.players?.find(p => p.id === scorer.player_id);
                return !!player;
              })
              .sort((a, b) => a.score_minute - b.score_minute);

            return (
              <Grid item xs={12} sm={6} key={match.id}>
                <StyledCard
                  sx={{
                    position: 'relative',
                    overflow: 'visible',
                    background: COLORS.gradient.dark,
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(218,165,32,0.1)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                    mb: { xs: 3, sm: 0 },
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 48px rgba(218,165,32,0.4)',
                    },
                  }}
                >
                  {/* Match Type Badge */}
                  <Chip
                    label={match.type || 'Group stage'}
                    sx={{
                      position: 'absolute',
                      top: -12,
                      left: 16,
                      background: COLORS.gradient.gold,
                      color: COLORS.background.dark,
                      fontWeight: 'bold',
                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      height: { xs: '24px', sm: '28px' },
                      boxShadow: '0 2px 8px rgba(218,165,32,0.3)',
                    }}
                  />

                  <Box sx={{ p: { xs: 3, sm: 3 }, pt: { xs: 4, sm: 4 } }}>
                    {/* Teams and Score */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      {/* Home Team */}
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            color: COLORS.text.primary,
                            fontSize: { xs: '0.9rem', sm: '1.1rem' },
                            fontWeight: 'bold',
                            mb: 1,
                          }}
                        >
                          {homeTeam?.team_name || 'Unknown'}
                        </Typography>
                        {homeTeamScorers.map((scorer, index) => (
                          <Typography
                            key={index}
                            variant="caption"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              color: COLORS.text.secondary,
                              fontSize: { xs: '0.65rem', sm: '0.75rem' },
                              mb: 0.5,
                            }}
                          >
                            <BallIcon
                              sx={{
                                fontSize: { xs: 12, sm: 14 },
                                color: COLORS.secondary,
                              }}
                            />
                            {scorer.player?.player_name || 'Unknown'} ({scorer.score_minute}')
                          </Typography>
                        ))}
                      </Box>

                      <Box
                        sx={{
                          px: { xs: 2, sm: 4 },
                          py: { xs: 1, sm: 2 },
                          minWidth: { xs: '90px', sm: '120px' },
                          maxWidth: { xs: '110px', sm: '160px' },
                          borderRadius: 2,
                          background: 'rgba(255,255,255,0.05)',
                          boxShadow: 'inset 0 0 10px rgba(0,0,0,0.2)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          position: 'relative',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '2px',
                            background: COLORS.gradient.gold,
                            borderRadius: '2px 2px 0 0',
                          },
                        }}
                      >
                        <Typography
                          variant="h4"
                          sx={{
                            color: '#ffffff',
                            fontWeight: 'bold',
                            fontSize: { xs: '1.25rem', sm: '2rem' },
                            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                            letterSpacing: { xs: 2, sm: 3 },
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {score ? `${score.home_team_score} - ${score.away_team_score}` : '0 - 0'}
                        </Typography>
                      </Box>

                      <Box sx={{ flex: 1, textAlign: 'right' }}>
                        <Typography
                          variant="h6"
                          sx={{
                            color: COLORS.text.highlight,
                            fontSize: { xs: '0.85rem', sm: '1.2rem' },
                            fontWeight: 'bold',
                            textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                            wordWrap: 'break-word',
                            lineHeight: { xs: 1.2, sm: 1.5 },
                            minHeight: { xs: '2.4em', sm: '3em' },
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {awayTeam?.team_name || 'Unknown'}
                        </Typography>
                        {/* Away Team Scorers */}
                        <Box sx={{ mt: 1 }}>
                          {awayTeamScorers.map((scorer, index) => (
                            <Typography
                              key={index}
                              variant="caption"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: 0.5,
                                color: COLORS.text.secondary,
                                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                mb: 0.5,
                              }}
                            >
                              {scorer.player?.player_name || 'Unknown'} ({scorer.score_minute}')
                              <BallIcon
                                sx={{
                                  fontSize: { xs: 12, sm: 14 },
                                  color: COLORS.secondary,
                                }}
                              />
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={{ mt: { xs: 1.5, sm: 3 }, px: { xs: 1, sm: 2 } }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: COLORS.text.muted,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          fontSize: { xs: '0.7rem', sm: '0.8rem' },
                          opacity: 0.9,
                          transition: 'opacity 0.2s ease',
                          '&:hover': {
                            opacity: 1,
                          },
                        }}
                      >
                        <StadiumIcon
                          sx={{
                            fontSize: { xs: 14, sm: 18 },
                            flexShrink: 0,
                            color: COLORS.text.accent,
                          }}
                        />
                        Field {match.field}
                      </Typography>
                    </Box>
                  </Box>
                </StyledCard>
              </Grid>
            );
          })}
      </Grid>
    </Box>
  );
};

export default OngoingMatches;
